import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Input, Textarea, Badge } from '@/backoffice/components/ui';
import { useAdminContext } from '@/backoffice/hooks/useAdminContext';
import {
  fetchAllUsers,
  fetchStaffMessages,
  sendStaffMessage,
  markStaffMessageRead,
  isStaffUser,
  isSuperAdminUser,
  type AdminUser,
  type AdminStaffMessage,
} from '@/domains/admin/services/admin.service';
import { formatRelativeTime } from '@/backoffice/utils';
import { Loader2, Send } from 'lucide-react';

export function AdminCommunicationPage() {
  const userType = useAdminContext();
  const isSuperAdmin = userType === 'super_admin';
  const [staff, setStaff] = useState<AdminUser[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [messages, setMessages] = useState<AdminStaffMessage[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const loadStaff = useCallback(async () => {
    const users = await fetchAllUsers();
    const filtered = users.filter((u) => {
      if (u.id === undefined) return false;
      if (isSuperAdmin) return isStaffUser(u) && !isSuperAdminUser(u);
      return isSuperAdminUser(u);
    });
    setStaff(filtered);
    if (filtered.length > 0 && !selectedId) {
      setSelectedId(filtered[0].id);
    }
  }, [isSuperAdmin, selectedId]);

  const loadMessages = useCallback(async (withUserId?: number) => {
    const res = await fetchStaffMessages(withUserId);
    setMessages(res.messages);
    setUnreadCount(res.unread_count);
    for (const msg of res.messages) {
      if (!msg.is_mine && !msg.read_at) {
        await markStaffMessageRead(msg.id);
      }
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    loadStaff()
      .catch(() => setStaff([]))
      .finally(() => setLoading(false));
  }, [loadStaff]);

  useEffect(() => {
    if (!selectedId) return;
    loadMessages(selectedId).catch(() => setMessages([]));
    const interval = setInterval(() => {
      loadMessages(selectedId).catch(() => undefined);
    }, 5000);
    return () => clearInterval(interval);
  }, [selectedId, loadMessages]);

  const handleSend = async () => {
    if (!selectedId || !subject.trim() || !body.trim()) return;
    setSending(true);
    try {
      await sendStaffMessage({ recipient_id: selectedId, subject: subject.trim(), body: body.trim() });
      setSubject('');
      setBody('');
      await loadMessages(selectedId);
    } finally {
      setSending(false);
    }
  };

  const selectedStaff = staff.find((s) => s.id === selectedId);

  return (
    <DashboardLayout
      userType={userType}
      title={isSuperAdmin ? 'Communication équipe' : 'Contact administrateur général'}
    >
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
      ) : staff.length === 0 ? (
        <Card><p className="text-center text-gray-500 py-12">Aucun interlocuteur disponible.</p></Card>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <Card padding="none" className="lg:col-span-1">
            <div className="p-4 border-b font-semibold flex items-center justify-between">
              <span>{isSuperAdmin ? 'Administrateurs' : 'Super-admin'}</span>
              {unreadCount > 0 && <Badge variant="danger">{unreadCount}</Badge>}
            </div>
            <ul className="divide-y max-h-[480px] overflow-y-auto">
              {staff.map((member) => (
                <li key={member.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(member.id)}
                    className={`w-full text-left p-4 hover:bg-gray-50 ${selectedId === member.id ? 'bg-indigo-50' : ''}`}
                  >
                    <p className="font-medium">{member.firstnames} {member.lastname}</p>
                    <p className="text-xs text-gray-500">{member.email ?? member.phone}</p>
                  </button>
                </li>
              ))}
            </ul>
          </Card>

          <Card className="lg:col-span-2 flex flex-col min-h-[520px]">
            {selectedStaff && (
              <>
                <div className="border-b pb-4 mb-4">
                  <p className="font-semibold">{selectedStaff.firstnames} {selectedStaff.lastname}</p>
                  <p className="text-sm text-gray-500">Messagerie interne sécurisée</p>
                </div>

                <div className="flex-1 space-y-3 overflow-y-auto max-h-72 mb-4">
                  {messages.length === 0 ? (
                    <p className="text-center text-gray-400 py-8">Aucun message — démarrez la conversation.</p>
                  ) : (
                    messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`p-3 rounded-lg max-w-[85%] ${msg.is_mine ? 'ml-auto bg-indigo-600 text-white' : 'bg-gray-100'}`}
                      >
                        <p className="text-xs opacity-80 mb-1">{msg.subject}</p>
                        <p className="text-sm">{msg.body}</p>
                        <p className={`text-xs mt-2 ${msg.is_mine ? 'text-indigo-200' : 'text-gray-400'}`}>
                          {msg.created_at ? formatRelativeTime(msg.created_at) : ''}
                        </p>
                      </div>
                    ))
                  )}
                </div>

                <div className="space-y-3 border-t pt-4">
                  <Input placeholder="Objet" value={subject} onChange={(e) => setSubject(e.target.value)} />
                  <Textarea placeholder="Votre message..." value={body} onChange={(e) => setBody(e.target.value)} />
                  <Button
                    leftIcon={<Send className="w-4 h-4" />}
                    isLoading={sending}
                    onClick={handleSend}
                    disabled={!subject.trim() || !body.trim()}
                  >
                    Envoyer
                  </Button>
                </div>
              </>
            )}
          </Card>
        </div>
      )}
    </DashboardLayout>
  );
}
